import ENV from "config/Env";
import { typedQueryClient, useTypedMutation } from "hooks";
import { DealersDashboardQueryKeys } from "modules/DealersDashboard/queryKeys";
import { ApiInstance } from "utils/Api";

import type { DealerID, DealerLocationID, ExactFailedInvoiceID } from "models";

type RetryExactInvoicePayload = {
  exact_failed_invoice_id?: ExactFailedInvoiceID;
  dealer_id?: DealerID;
  dealer_location_id?: DealerLocationID;
};

export const useRetryExactInvoiceMutation = () =>
  useTypedMutation<RetryExactInvoicePayload, void>({
    mutationFn: async payload => (await ApiInstance.post("/invoices/failed/retry", payload, ENV.exactBaseURL)).data,
    onSuccess: ({ exact_failed_invoice_id, dealer_id, dealer_location_id }) => {
      if (!exact_failed_invoice_id) {
        if (dealer_id) typedQueryClient.invalidateQueries(DealersDashboardQueryKeys.dealerDetails(dealer_id));
        if (dealer_location_id) typedQueryClient.invalidateQueries(DealersDashboardQueryKeys.locationDetails(dealer_location_id));
        return;
      }

      if (dealer_id) {
        const dealer = typedQueryClient.getQueryData(DealersDashboardQueryKeys.dealerDetails(dealer_id));
        const failedInvoiceIdx = dealer?.exact_failed_invoices?.findIndex(invoice => invoice.id === exact_failed_invoice_id) ?? -1;
        if (!dealer?.exact_failed_invoices || failedInvoiceIdx === -1) return;

        const exactFailedInvoices = dealer.exact_failed_invoices.toSpliced(failedInvoiceIdx, 1);

        typedQueryClient.setQueryData(DealersDashboardQueryKeys.dealerDetails(dealer_id), {
          ...dealer,
          exact_failed_invoices: exactFailedInvoices,
          has_failed_invoices: !!exactFailedInvoices.length
        });
      }

      if (dealer_location_id) {
        const location = typedQueryClient.getQueryData(DealersDashboardQueryKeys.locationDetails(dealer_location_id));
        const failedInvoiceIdx = location?.exact_failed_invoices?.findIndex(invoice => invoice.id === exact_failed_invoice_id) ?? -1;
        if (!location?.exact_failed_invoices || failedInvoiceIdx === -1) return;

        const exactFailedInvoices = location.exact_failed_invoices.toSpliced(failedInvoiceIdx, 1);

        typedQueryClient.setQueryData(DealersDashboardQueryKeys.locationDetails(dealer_location_id), {
          ...location,
          exact_failed_invoices: exactFailedInvoices,
          has_failed_invoices: !!exactFailedInvoices.length
        });
      }
    }
  });
